import { createContext, useContext, useMemo } from 'react';
import { listTime as listTimeRaw, messageTime as messageTimeRaw } from './commsFormat';
import {
  type DateFormats,
  formatDate,
  formatTime,
  resolveFormats,
} from './dateFormat';
import { fmtDateTime as fmtDateTimeRaw, fmtWallTime as fmtWallTimeRaw } from './format';
import { sameClock, zoneAbbr, zoneLabel } from './timeZone';

/**
 * How this tenant reads a clock: the DISPATCH timezone plus the two display
 * patterns, applied to every date and time the console and the embedded module
 * render.
 *
 * Dispatch stores the zone on the tenant (`public.tenants.dispatch_time`) and
 * the patterns in its System Configs (`date-format` / `time-format`), and every
 * dispatch surface reads all three — so a dispatcher with the dashboard and the
 * CRM side by side must see one clock, in one hand. Not the browser's, which is
 * whatever laptop they happen to be on, and not this module's own taste, which
 * would put "2 Sep, 15:00" beside the dashboard's "02/09/2026 03:00 pm".
 *
 * Null zone = dispatch has none configured; everything then falls back to the
 * viewer's own zone, which is what the console did before this existed. The
 * PATTERNS have no such hole — an unset row is dispatch's own default.
 *
 * **Never format a date outside this.** A component that reaches for
 * `toLocaleString` gets the viewer's locale and shape, which is the whole bug.
 *
 * Provider: components/TenantTimeProvider.
 */
export interface TenantTime {
  zone: string | null;
  /** "America/New_York · EDT" — for the one place that names the zone outright. */
  zoneLabel: string | null;
  /** The tenant's raw patterns, for the rare caller that renders its own shape. */
  formats: DateFormats;
  fmtDateTime: (iso: string | null) => string;
  /** Just the calendar day: "02/09/2026". */
  fmtDate: (iso: string | null) => string;
  /** Just the clock: "03:05 pm". */
  fmtTime: (iso: string | null) => string;
  /**
   * A zone-LESS wall time (`YYYY-MM-DDTHH:mm`) in the tenant's shape, rendered
   * exactly as written — see lib/format.
   */
  fmtWallTime: (wall: string) => string;
  listTime: (iso: string | null, now?: Date) => string;
  messageTime: (iso: string | null, now?: Date) => string;
  /**
   * Short label for a timestamp that carries its OWN zone (a booking's pickup
   * zone), or null when that zone reads the same clock as the display zone at
   * that moment — a badge saying "EDT" next to every New York pickup is noise.
   */
  foreignZoneAbbr: (zone: string | null | undefined, at?: Date) => string | null;
}

export const TenantTimeContext = createContext<TenantTime | null>(null);

/** The viewer's own zone — the comparison baseline when dispatch has none. */
const viewerZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const parse = (iso: string | null): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

export function buildTenantTime(
  zone: string | null,
  rawFormats?: Partial<DateFormats> | null,
): TenantTime {
  const displayZone = zone ?? viewerZone();
  const formats = resolveFormats(rawFormats);
  return {
    zone,
    zoneLabel: zoneLabel(zone),
    formats,
    fmtDateTime: (iso) => fmtDateTimeRaw(iso, zone, formats),
    fmtDate: (iso) => {
      const date = parse(iso);
      return date ? formatDate(date, zone, formats.dateFormat) : '—';
    },
    fmtTime: (iso) => {
      const date = parse(iso);
      return date ? formatTime(date, zone, formats.timeFormat) : '—';
    },
    fmtWallTime: (wall) => fmtWallTimeRaw(wall, formats),
    listTime: (iso, now) => listTimeRaw(iso, now ?? new Date(), zone, formats),
    messageTime: (iso, now) => messageTimeRaw(iso, now ?? new Date(), zone, formats),
    foreignZoneAbbr: (other, at = new Date()) =>
      sameClock(other, displayZone, at) ? null : zoneAbbr(other, at),
  };
}

/**
 * Timestamp formatters bound to the tenant's zone. Outside a provider it falls
 * back to the viewer's zone rather than throwing — a missing provider must not
 * blank a screen over a clock label.
 */
export function useTimeFormat(): TenantTime {
  const ctx = useContext(TenantTimeContext);
  const fallback = useMemo(() => buildTenantTime(null, null), []);
  return ctx ?? fallback;
}
